import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { LANGUAGE_COOKIE, supportedLanguages } from '../i18n';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { usePenpal } from '../hooks/usePenpal';

const flags: Record<string, string> = {
  en: '🇺🇸',
  de: '🇩🇪',
  fr: '🇫🇷',
  es: '🇪🇸',
  ja: '🇯🇵',
};

interface LanguageSwitcherProps {
  variant?: 'ghost' | 'outline';
  size?: 'sm' | 'default' | 'icon';
  className?: string;
}

export function LanguageSwitcher({ variant = 'outline', size = 'sm', className }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const { isInIframe, getSettings, setSettings } = usePenpal();
  const currentLang = supportedLanguages.find((l) => l.code === i18n.language);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        if (!isInIframe) return;
        const settings = await getSettings?.();
        const lang = (settings as Record<string, unknown>)?.[LANGUAGE_COOKIE];
        if (!cancelled && typeof lang === 'string' && lang && lang !== i18n.language) {
          await i18n.changeLanguage(lang);
        }
      } catch {
        // ignore, cookie-based init remains
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [getSettings, i18n, isInIframe]);

  const changeLanguage = async (code: string) => {
    await i18n.changeLanguage(code);
    if (isInIframe) {
      try {
        await setSettings?.(LANGUAGE_COOKIE, code);
      } catch {
        // ignore, cookie-based persistence remains
      }
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size={size} className={className}>
          <Globe className="h-4 w-4 mr-1" />
          <span className={`text-base leading-none ${size === 'icon' ? '' : 'mr-1'}`}>{flags[i18n.language]}</span>
          {size !== 'icon' && <span>{currentLang?.label ?? i18n.language}</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {supportedLanguages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => void changeLanguage(lang.code)}
            className={i18n.language === lang.code ? 'font-semibold bg-accent' : ''}
          >
            <span className="mr-2 text-base">{flags[lang.code]}</span>
            {lang.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
